import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import classNames from 'classnames';
import { Form as FinalForm } from 'react-final-form';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { required } from '../../util/validators';
import {
  region,
  REGION,
  vatNumberMatchesCountry,
  vatPrefixForCountry,
  billingFromInvoicing,
} from '../../util/vat';
import getCountryCodes from '../../translations/countryCodes';
import {
  Form,
  FieldRadioButton,
  FieldSelect,
  FieldTextInput,
  PrimaryButton,
} from '../../components';

import css from './BillingDetailsForm.css';

const normalizeVat = v => (typeof v === 'string' ? v.replace(/[\s.\-]/g, '').toUpperCase() : v);
const clean = v => (typeof v === 'string' && v.trim() ? v.trim() : null);

// The privateData.billing record (spec §2) from the form values.
export const billingFromValues = (values, now = new Date()) => {
  const isBusiness = values.customerType === 'BUSINESS';
  return {
    customerType: isBusiness ? 'BUSINESS' : 'CONSUMER',
    legalName: isBusiness ? clean(values.legalName) : null,
    country: (values.country || '').trim().toUpperCase(),
    postalCode: (values.postalCode || '').trim(),
    street: isBusiness ? clean(values.street) : null,
    city: isBusiness ? clean(values.city) : null,
    vatNumber: isBusiness ? clean(normalizeVat(values.vatNumber || '')) : null,
    businessProofRef: isBusiness ? clean(values.businessProofRef) : null,
    updatedAt: now.toISOString(),
  };
};

// Country select options are ISO 3166-1 alpha-2 codes; a fallback country
// (for example the Mapbox short code the customer picked at signup) must be
// one of them or the select would show an empty value.
const KNOWN_COUNTRY_CODES = new Set(getCountryCodes('en').map(c => c.code));
const knownCountry = code => {
  const upper = typeof code === 'string' ? code.trim().toUpperCase() : '';
  return KNOWN_COUNTRY_CODES.has(upper) ? upper : null;
};

// Most Freedomly customers buy for a company, so an empty form starts as a
// business purchase; a stored profile keeps whatever type it saved.
export const initialValuesFromBilling = (billing, fallback = {}) => {
  const b = billing || {};
  return {
    customerType: b.customerType || 'BUSINESS',
    legalName: b.legalName || fallback.legalName || '',
    country: b.country || knownCountry(fallback.country) || 'FI',
    postalCode: b.postalCode || fallback.postalCode || '',
    street: b.street || fallback.street || '',
    city: b.city || fallback.city || '',
    vatNumber: b.vatNumber || fallback.vatNumber || '',
    businessProofRef: b.businessProofRef || '',
  };
};

// Prefill for a user: their billing profile, else one derived from an old
// invoicing request (spec §2), else an empty form in the country they gave
// at signup (publicData.locationCountryCode), else Finland.
export const billingInitialValuesForUser = currentUser => {
  const profile = (currentUser && currentUser.attributes && currentUser.attributes.profile) || {};
  const privateData = profile.privateData || {};
  const publicData = profile.publicData || {};
  return initialValuesFromBilling(
    privateData.billing || billingFromInvoicing(privateData.invoicing),
    { country: publicData.locationCountryCode }
  );
};

const BillingDetailsFormComponent = props => (
  <FinalForm
    {...props}
    onSubmit={values => props.onSubmit(billingFromValues(values))}
    render={fieldRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        values,
        invalid,
        inProgress,
        saveError,
        compact,
        lockedFields,
        lockedNote,
        intl,
      } = fieldRenderProps;
      const isBusiness = values.customerType === 'BUSINESS';
      const locked = name => lockedFields.includes(name);
      const r = region({ country: values.country, postalCode: values.postalCode });
      const showVatNumber = isBusiness && (r === REGION.FI || r === REGION.EU);
      const showProof = isBusiness && (r === REGION.AX || r === REGION.NON_EU);
      const requiredMsg = intl.formatMessage({ id: 'BillingDetailsForm.required' });
      // The number must be the billing country's own (regions.js): a German
      // number on a Finnish address would be priced as a Finnish sale.
      const vatNumberCountry = (value, allValues) =>
        !value || vatNumberMatchesCountry(value, allValues.country)
          ? undefined
          : intl.formatMessage(
              { id: 'BillingDetailsForm.vatNumberCountryMismatch' },
              { prefix: vatPrefixForCountry(allValues.country) }
            );
      const id = name => (formId ? `${formId}.${name}` : name);
      const classes = classNames(rootClassName || css.root, className, {
        [css.compact]: compact,
      });

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          {!compact ? (
            <p className={css.intro}>
              <FormattedMessage id="BillingDetailsForm.intro" />
            </p>
          ) : null}
          <div className={css.field}>
            <label className={css.groupLabel}>
              <FormattedMessage id="BillingDetailsForm.customerTypeLabel" />
            </label>
            <div className={css.radioOptions}>
              <FieldRadioButton
                id={id('customerType.business')}
                name="customerType"
                value="BUSINESS"
                label={intl.formatMessage({ id: 'BillingDetailsForm.customerTypeBusiness' })}
                disabled={locked('customerType')}
              />
              <FieldRadioButton
                id={id('customerType.consumer')}
                name="customerType"
                value="CONSUMER"
                label={intl.formatMessage({ id: 'BillingDetailsForm.customerTypeConsumer' })}
                disabled={locked('customerType')}
              />
            </div>
          </div>
          {lockedNote ? <p className={css.lockedNote}>{lockedNote}</p> : null}
          {isBusiness ? (
            <FieldTextInput
              type="text"
              id={id('legalName')}
              name="legalName"
              className={css.field}
              label={intl.formatMessage({ id: 'BillingDetailsForm.legalNameLabel' })}
              validate={required(requiredMsg)}
              disabled={locked('legalName')}
            />
          ) : null}
          <FieldSelect
            id={id('country')}
            name="country"
            className={css.field}
            label={intl.formatMessage({ id: 'BillingDetailsForm.countryLabel' })}
            validate={required(requiredMsg)}
          >
            {getCountryCodes(intl.locale).map(c => (
              <option key={c.code} value={c.code}>
                {c.name}
              </option>
            ))}
          </FieldSelect>
          {isBusiness ? (
            <FieldTextInput
              type="text"
              id={id('street')}
              name="street"
              className={css.field}
              label={intl.formatMessage({ id: 'BillingDetailsForm.streetLabel' })}
              validate={required(requiredMsg)}
            />
          ) : null}
          <div className={css.row}>
            <FieldTextInput
              type="text"
              id={id('postalCode')}
              name="postalCode"
              className={css.field}
              label={intl.formatMessage({ id: 'BillingDetailsForm.postalCodeLabel' })}
              validate={required(requiredMsg)}
            />
            {isBusiness ? (
              <FieldTextInput
                type="text"
                id={id('city')}
                name="city"
                className={css.field}
                label={intl.formatMessage({ id: 'BillingDetailsForm.cityLabel' })}
                validate={required(requiredMsg)}
              />
            ) : null}
          </div>
          {showVatNumber ? (
            <div className={css.field}>
              <FieldTextInput
                type="text"
                id={id('vatNumber')}
                name="vatNumber"
                label={intl.formatMessage({ id: 'BillingDetailsForm.vatNumberLabel' })}
                placeholder="FI12345678"
                format={normalizeVat}
                formatOnBlur
                validate={vatNumberCountry}
              />
              <p className={css.help}>
                <FormattedMessage id="BillingDetailsForm.vatNumberHelp" />
              </p>
            </div>
          ) : null}
          {showProof ? (
            <div className={css.field}>
              <FieldTextInput
                type="text"
                id={id('businessProofRef')}
                name="businessProofRef"
                label={intl.formatMessage({ id: 'BillingDetailsForm.businessProofLabel' })}
              />
              <p className={css.help}>
                <FormattedMessage id="BillingDetailsForm.businessProofHelp" />
              </p>
            </div>
          ) : null}
          {saveError ? (
            <p className={css.error}>
              <FormattedMessage id="BillingDetailsForm.saveError" />
            </p>
          ) : null}
          <PrimaryButton
            type="submit"
            className={css.submit}
            inProgress={inProgress}
            disabled={invalid || inProgress}
          >
            <FormattedMessage id="BillingDetailsForm.save" />
          </PrimaryButton>
        </Form>
      );
    }}
  />
);

BillingDetailsFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  formId: null,
  inProgress: false,
  saveError: null,
  compact: false,
  lockedFields: [],
  lockedNote: null,
};
BillingDetailsFormComponent.propTypes = {
  rootClassName: PropTypes.string,
  className: PropTypes.string,
  formId: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  inProgress: PropTypes.bool,
  saveError: PropTypes.object,
  compact: PropTypes.bool,
  lockedFields: PropTypes.arrayOf(PropTypes.string),
  lockedNote: PropTypes.node,
  intl: intlShape.isRequired,
};

const BillingDetailsForm = compose(injectIntl)(BillingDetailsFormComponent);
BillingDetailsForm.displayName = 'BillingDetailsForm';
export default BillingDetailsForm;
